<script setup lang="ts">
import { useAdmissions } from '~/composables/useAdmissions'
import { useFormations } from '~/composables/useFormations'
import AdmissionsProcedure from '~/components/admissions/AdmissionsProcedure.vue'
import TuitionFees from '~/components/global/TuitionFees.vue'
import CandidatureFormManager from '~/components/admissions/CandidatureFormManager.vue'
import AppCertifications from '~/components/ui/AppCertifications.vue'
import SchoolPromoVideo from '~/components/school/SchoolPromoVideo.vue'

const { getAdmissionsConfig } = useAdmissions()
const { getAllFormations } = useFormations()

// Fetch admissions config from API (SSR)
const admissionsData = await getAdmissionsConfig()
const steps = admissionsData.steps
const nextSession = admissionsData.nextSession

// Formations logic (SSR)
const { data: formationsData } = await useAsyncData('admissions-formations', () => getAllFormations())
const formations = computed(() => formationsData.value || [])

const { getAll: getAllVideos } = useSiteVideos()
const { data: siteVideos } = await useAsyncData('site-videos', () => getAllVideos())
const sectionVideoUrl = computed(() => siteVideos.value?.find(v => v.location === 'candidature-section')?.videoUrl || '')

const { setPageSeo } = useSeo()
await setPageSeo({
  title: 'Candidature & Admission',
  description: 'Rejoignez l\'école OXO. Découvrez les étapes d\'admission, les tarifs et postulez pour la prochaine rentrée.'
})
</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <!-- Hero Section -->
    <UiPageHero 
      pageKey="candidature"
      badge-text="Admission & Candidature"
      title-prefix="DEVENEZ"
      title-highlight="PROFESSIONNEL"
      description="Faites le premier pas pour transformer votre passion en métier et intégrer notre prochaine promotion."
      :tags="['SESSION 2026', 'MÉTIER', 'AVENIR']"
      height="screen"
      align="left"
      :primary-button="{ text: 'DÉPOSER MON DOSSIER', to: '#candidature-form', umamiEvent: 'Candidature - Hero - Form' }"
      :secondary-button="{ text: 'RETOUR À L\'ACCUEIL', to: '/', umamiEvent: 'Candidature - Hero - Home' }"
    />

    <!-- Procedure Section (Steps only - fees moved to formation pages) -->
    <AdmissionsProcedure 
      :steps="steps" 
      :next-session="nextSession" 
    />

    <!-- Fees Section -->
    <TuitionFees />

    <!-- Accreditation Reassurance -->
    <section class="py-16 bg-bacground-dim/5 border-b border-oxo-border">
      <div class="container mx-auto px-6 flex flex-col items-start gap-12">
        <UiSectionHeader
          align="left"
          titlePrefix="Certification"
          titleHighlight="& Qualité"
          subtitle="Notre organisme de formation répond aux exigences de qualité fixées par l'État et la Région."
        />
        <AppCertifications centered />
      </div>
    </section>

    <!-- Form Section -->
    <!-- Form Section -->
    <ClientOnly>
      <CandidatureFormManager 
        :formations="formations" 
        :next-session="nextSession" 
      />
    </ClientOnly>

    <section class="py-20 bg-background/50 border-t border-oxo-border overflow-hidden">
      <div class="container mx-auto px-6 text-center">
        <h2 class="font-display font-bold text-3xl text-foreground mb-16 animate-fade-in-up">
          Besoin d'aide pour votre candidature ?
        </h2>
        
        <div class="mb-16">
          <AppContactInfo class="grid-cols-1! md:grid-cols-2! lg:grid-cols-4! dark:text-white" />
        </div>

        <div class="flex justify-center mt-12 animate-fade-in-up" style="animation-delay: 0.4s">
          <AppButton to="/contact" variant="secondary" size="lg">
            Contact
            <i class="fas fa-paper-plane ml-2 pl-1 text-sm" aria-hidden="true"></i>
          </AppButton>
        </div>
      </div>
    </section>

    <SchoolPromoVideo
        v-if="sectionVideoUrl"
        :video-url="sectionVideoUrl"
    />
  </div>
</template>
